/**
 * qf-model.ts — Queue-flow model: the SIBLING of the messaging flow builder's
 * fb-model, adapted to the queue lifecycle (operator decision 2026-07-17:
 * same visual language, queue-specific model).
 *
 * Structural reuse: step shapes are layout-compatible with fb-model's FBStep
 * (same `kind` discriminants for comms/wait/decision/survey/exit + tree helpers
 * with identical semantics), so the queue builder reuses fb-layout's pure
 * auto-layout engine unchanged. The queue-specific additions are:
 *   • "stage" steps — the queue lifecycle states (waiting / called / serving /
 *     done) that track wait time and service time,
 *   • queue triggers, queue decision conditions, queue exit outcomes
 *     (Served / Left queue / No-show — the Unserved states).
 */

export const QF_NODE_W = 272;
import { qpDefaultCfg, type QPageId } from "./qp-pages";

export interface QFTrigger { id: string; label: string; sub: string; }
// Triggers reference the journey's SCREENS (PM direction 2026-07-17) — the old
// lifecycle-event triggers (joined/transferred/rejoined) are migrated below.
export const QF_TRIGGERS: QFTrigger[] = [
  { id: "screen_services", label: "Services screen is opened",       sub: "QR code, kiosk or link — the journey starts on the Services screen" },
  { id: "screen_wait",     label: "Wait Time screen is opened",      sub: "The service is pre-picked (single-service queue or staff link) — start at the wait estimate" },
  { id: "screen_contact",  label: "Contact Details screen is opened", sub: "Service and wait already known — start at contact capture" },
  { id: "screen_waiting",  label: "Waiting in queue screen is opened", sub: "Staff added the customer — the journey starts already in the queue" },
];
const QF_TRIGGER_MIGRATE: Record<string, string> = { joined: "screen_services", transferred: "screen_waiting", rejoined: "screen_services" };
export const QF_TRIGGER = (id: string) => QF_TRIGGERS.find((t) => t.id === (QF_TRIGGER_MIGRATE[id] ?? id)) ?? QF_TRIGGERS[0];

/** In-flow trigger steps (operator 2026-07-21): lifecycle moments placed ON the
 *  canvas — the steps after a trigger run when that moment happens. */
export const QF_FLOW_TRIGGERS: { id: string; label: string; sub: string }[] = [
  { id: "joined",  label: "Joined the Queue",      sub: "Fires the moment the customer joins" },
  { id: "update",  label: "Queue Update",          sub: "Fires when their position or wait changes" },
  { id: "left",    label: "Left without Service",  sub: "Fires when they leave unserved (left / no-show)" },
  { id: "served",  label: "Service Delivery",     sub: "Fires when the service is delivered" },
];
export const QF_FLOW_TRIGGER = (id: string) => QF_FLOW_TRIGGERS.find((t) => t.id === id) ?? QF_FLOW_TRIGGERS[0];

/** Queue lifecycle stages — first-class nodes on the canvas. */
export type QFStageId = "waiting" | "called" | "serving" | "done";
export const QF_STAGES: Record<QFStageId, { label: string; sub: string; color: string; tint: string }> = {
  waiting: { label: "Waiting",           sub: "Wait time is tracked from here",    color: "#C8861E", tint: "#FAF1DE" },
  called:  { label: "It's their turn",   sub: "Customer is called up",             color: "#2D60EB", tint: "#E8EEFD" },
  serving: { label: "Being served",      sub: "Service time is tracked from here", color: "#1F8A5B", tint: "#E4F5EF" },
  done:    { label: "Service completed", sub: "Checked out — by staff or auto",    color: "#475569", tint: "#E7ECF2" },
};
export const QF_STAGE_ORDER: QFStageId[] = ["waiting", "called", "serving", "done"];

export const QF_DECISION_CONDS = [
  "Customer checks in",
  "Waited more than 10 minutes",
  "Waited more than 20 minutes",
  "Replied to the last message",
  "No answer after 2 calls",
  "Party size is over 4",
  "Answered the booking questions",
  "Is a repeat visitor",
];
export const QF_EXIT_OUTCOMES = ["Served", "Left the queue", "No-show", "Transferred out", "Removed by staff"];
export const QF_WAIT_UNITS = ["Minutes", "Hours", "Days"];
export const QF_WAIT_TIMINGS = [
  { value: "after_prev",    label: "After the previous step" },
  { value: "after_called",  label: "After the customer is called" },
  { value: "after_service", label: "After the service ends" },
];
export const QF_SENDWHEN = ["Immediately", "After previous step", "When the stage is reached"];

let _qfSeq = 0;
const qfId = (p: string) => `${p}_${Date.now().toString(36)}${(_qfSeq++).toString(36)}${Math.random().toString(36).slice(2, 4)}`;

export interface QStepStage   { id: string; kind: "stage";    stage: QFStageId; }
export interface QStepTrigger { id: string; kind: "qtrigger"; event: string; }
export interface QStepPage    { id: string; kind: "page";     page: QPageId; cfg: Record<string, unknown>; }
export interface QStepComms   { id: string; kind: "comms";    messageId: string | null; sendWhen: string; landingId: string; }
export interface QStepWait    { id: string; kind: "wait";     amount: number; unit: string; timing: string; }
export interface QStepDecision{ id: string; kind: "decision"; condition: string; yes: QFStep[]; no: QFStep[]; }
export interface QStepSurvey  { id: string; kind: "survey";   surveyId: string | null; sendWhen: string; }
export interface QStepExit    { id: string; kind: "exit";     outcome: string; note: string; }
export type QFStep = QStepStage | QStepTrigger | QStepPage | QStepComms | QStepWait | QStepDecision | QStepSurvey | QStepExit;

export interface QFFlow { trigger: { event: string }; steps: QFStep[]; }

export const qfMkStage    = (stage: QFStageId): QStepStage => ({ id: qfId("st"), kind: "stage", stage });
export const qfMkTrigger  = (event = "joined"): QStepTrigger => ({ id: qfId("tg"), kind: "qtrigger", event });
export const qfMkPage     = (page: QPageId, cfg?: Record<string, unknown>): QStepPage => ({ id: qfId("pg"), kind: "page", page, cfg: cfg ?? qpDefaultCfg(page) });
export const qfMkComms    = (messageId: string | null = null): QStepComms => ({ id: qfId("c"), kind: "comms", messageId, sendWhen: "Immediately", landingId: "" });
export const qfMkWait     = (): QStepWait => ({ id: qfId("w"), kind: "wait", amount: 5, unit: "Minutes", timing: "after_prev" });
export const qfMkDecision = (): QStepDecision => ({ id: qfId("d"), kind: "decision", condition: QF_DECISION_CONDS[0], yes: [], no: [] });
export const qfMkSurvey   = (surveyId: string | null = null): QStepSurvey => ({ id: qfId("sv"), kind: "survey", surveyId, sendWhen: "Immediately" });
export const qfMkExit     = (outcome = "Served"): QStepExit => ({ id: qfId("x"), kind: "exit", outcome, note: "" });

/** Default flow — the full page-based customer journey:
 *  Services → Wait Time → Contact Details → join confirmation → Waiting in
 *  queue → turn-coming-up → it's-your-turn → thank-you → How did we do? */
export function qfDefaultFlow(): QFFlow {
  return {
    trigger: { event: "screen_services" },
    steps: [
      qfMkPage("services"),
      qfMkPage("wait"),
      qfMkPage("contact"),
      qfMkComms(),                 // queue-join confirmation
      qfMkPage("waiting"),
      qfMkPage("noservice"),      // required — shown when the customer leaves unserved
      qfMkComms(),                 // your turn is coming up
      qfMkComms(),                 // it's your turn
      qfMkComms(),                 // thank you for your business
      qfMkPage("csat"),
    ],
  };
}

/** Per-TYPE default flows — operator-built REFERENCE implementations, captured
 *  verbatim 2026-07-21:
 *  · quick    = the "Quick Queue test" queue's flow (stock hero image omitted —
 *               the queue hero / gradient tops the pages instead).
 *  · standard = the "Me Nombre es Queue" queue's flow. */
export function qfTypeDefaultFlow(typeId: string): QFFlow {
  if (typeId === "quick") {
    return {
      trigger: { event: "screen_services" },
      steps: [
        qfMkPage("services"),
        qfMkPage("contact"),
        qfMkTrigger("joined"),
        qfMkComms("msg_q_join"),
        qfMkPage("waiting", { ...qpDefaultCfg("waiting"), allowMessaging: false }),
        qfMkComms("msg_q_turn"),
        qfMkTrigger("served"),
        qfMkExit("Served"),
      ],
    };
  }
  const dec = qfMkDecision(); // "Customer checks in"
  dec.yes = [
    qfMkTrigger("served"),
    qfMkPage("csat"),
    { ...qfMkWait(), amount: 10, timing: "after_service" },
    { ...qfMkComms("msg_q_thanks"), sendWhen: "After previous step" },
    qfMkExit("Served"),
  ];
  dec.no = [
    { ...qfMkWait(), amount: 2, timing: "after_called" },
    qfMkComms("msg_q_turn"),
    { ...qfMkWait(), amount: 2, timing: "after_prev" },
    qfMkPage("noservice"),
    qfMkComms("msg_q_missed"),
    qfMkTrigger("left"),
    qfMkExit("No-show"),
  ];
  return {
    trigger: { event: "screen_services" },
    steps: [
      qfMkPage("services"),
      qfMkPage("wait"),
      qfMkPage("contact"),
      qfMkTrigger("joined"),
      qfMkComms("msg_q_join"),
      qfMkPage("waiting"),
      qfMkComms("msg_q_turn"),
      dec,
    ],
  };
}

/** Migrate a stored flow: old lifecycle-stage spines become page spines and
 *  old trigger ids map to their screen equivalents. Idempotent. (The
 *  Left-without-service page is seeded by default but DELETABLE — operator
 *  2026-07-21; when absent, the public surface falls back to the default
 *  left-without-service card.) */
export function qfMigrate(flow: QFFlow): QFFlow {
  const trigger = { event: QF_TRIGGER(flow.trigger?.event ?? "screen_services").id };
  const stageToPage: Partial<Record<QFStageId, QPageId>> = { waiting: "waiting", done: "csat" };
  const walk = (arr: QFStep[]): QFStep[] => arr.flatMap((s): QFStep[] => {
    if (s.kind === "stage") { const p = stageToPage[s.stage]; return p ? [{ id: s.id, kind: "page", page: p, cfg: qpDefaultCfg(p) }] : []; }
    if (s.kind === "page" && !s.cfg) return [{ ...s, cfg: qpDefaultCfg(s.page) }];
    if (s.kind === "decision") return [{ ...s, yes: walk(s.yes), no: walk(s.no) }];
    return [s];
  });
  return { trigger, steps: walk(flow.steps ?? []) };
}

/* ── Tree helpers (identical semantics to fb-model) ─────────────────── */
export function qfFindStep(steps: QFStep[], id: string): QFStep | null {
  for (const s of steps) {
    if (s.id === id) return s;
    if (s.kind === "decision") { const r = qfFindStep(s.yes, id) || qfFindStep(s.no, id); if (r) return r; }
  }
  return null;
}
export function qfEdit(steps: QFStep[], id: string, fn: (s: QFStep) => QFStep): QFStep[] {
  return steps.map((s) => {
    if (s.id === id) return fn(s);
    if (s.kind === "decision") return { ...s, yes: qfEdit(s.yes, id, fn), no: qfEdit(s.no, id, fn) };
    return s;
  });
}
export function qfRemove(steps: QFStep[], id: string): QFStep[] {
  return steps.filter((s) => s.id !== id).map((s) =>
    s.kind === "decision" ? { ...s, yes: qfRemove(s.yes, id), no: qfRemove(s.no, id) } : s);
}
export interface QInsertDesc { parentId: string | null; branch: "yes" | "no" | null; index: number; }
export function qfInsert(steps: QFStep[], desc: QInsertDesc, step: QFStep): QFStep[] {
  if (!desc.parentId) { const n = steps.slice(); n.splice(desc.index, 0, step); return n; }
  return steps.map((s) => {
    if (s.id === desc.parentId && s.kind === "decision") {
      const arr = (desc.branch === "yes" ? s.yes : s.no).slice();
      arr.splice(desc.index, 0, step);
      return desc.branch === "yes" ? { ...s, yes: arr } : { ...s, no: arr };
    }
    if (s.kind === "decision") return { ...s, yes: qfInsert(s.yes, desc, step), no: qfInsert(s.no, desc, step) };
    return s;
  });
}

export function qfWaitLabel(s: QStepWait): string {
  const unit = String(s.unit || "").toLowerCase();
  const qty = `${s.amount} ${unit}`;
  if (s.timing === "after_called") return `${qty} after the call`;
  if (s.timing === "after_service") return `${qty} after the service`;
  return `Wait ${qty}`;
}
